<template>
  <div class="tw-bg-white tw-shadow-md tw-rounded-xl tw-p-6 tw-border tw-border-slate-100">
    <div class="tw-flex tw-items-start tw-space-x-3 tw-mb-4">
      <div class="tw-flex tw-h-10 tw-w-10 tw-items-center tw-justify-center tw-rounded-full tw-bg-blue-50">
        <svg
          class="tw-h-5 tw-w-5 tw-text-blue-600"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M12 6v6l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      </div>
      <div>
        <h3 class="tw-text-base tw-font-semibold tw-text-slate-900">
          Step 1 — Review &amp; confirm
        </h3>
        <p class="tw-mt-1 tw-text-sm tw-text-slate-600">
          Check the products, quantities and pricing in your quote. When you are satisfied that everything is correct, confirm below to move to payment.
        </p>
      </div>
    </div>
    <button
        v-if="canRequestAmendments"
        type="button"
        class="tw-w-full tw-mb-3 tw-inline-flex tw-justify-center tw-rounded-md tw-border tw-border-slate-200 tw-bg-white tw-px-4 tw-py-2.5 tw-text-sm tw-font-medium tw-text-slate-700 tw-shadow-sm hover:tw-bg-slate-50 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-ring-offset-2"
        @click="$emit('open-amendments')"
    >
      Request Amendments
    </button>

    <!-- Specification confirmation -->
    <div class="tw-bg-slate-50 tw-rounded-lg tw-p-4 tw-mb-4">
      <label class="tw-flex tw-items-start tw-cursor-pointer">
        <input
          :checked="confirmationChecked"
          type="checkbox"
          class="tw-mt-1 tw-mr-3 tw-h-4 tw-w-4 tw-text-blue-600 tw-border-slate-300 tw-rounded focus:tw-ring-blue-500"
          @change="$emit('update:confirmationChecked', $event.target.checked)"
        >
        <span class="tw-text-sm tw-text-slate-700 tw-text-left">
          I have reviewed the quote details shown and confirm that the specification is correct.
        </span>
      </label>
    </div>

    <!-- Authorization approval -->
    <div class="tw-bg-slate-50 tw-rounded-lg tw-p-4 tw-mb-4">
      <label class="tw-flex tw-items-start tw-cursor-pointer">
        <input
          :checked="approvedToSign"
          type="checkbox"
          class="tw-mt-1 tw-mr-3 tw-h-4 tw-w-4 tw-text-blue-600 tw-border-slate-300 tw-rounded focus:tw-ring-blue-500"
          @change="$emit('update:approvedToSign', $event.target.checked)"
        >
        <span class="tw-text-sm tw-text-slate-700 tw-text-left">
          I am authorised to approve this order
        </span>
      </label>
      <div
        v-if="approvedToSign"
        class="tw-mt-3 tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-3"
      >
        <input
          :value="approverName"
          type="text"
          placeholder="Your name"
          class="tw-w-full tw-border tw-border-slate-300 tw-p-2 tw-rounded tw-text-sm focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500"
          @input="$emit('update:approverName', $event.target.value)"
        >
        <input
          :value="approverEmail"
          type="email"
          placeholder="Your email"
          class="tw-w-full tw-border tw-border-slate-300 tw-p-2 tw-rounded tw-text-sm focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500"
          @input="$emit('update:approverEmail', $event.target.value)"
        >
      </div>
    </div>

    <!-- Terms and Conditions -->
    <div class="tw-mb-4">
      <div
        class="tw-rounded-lg tw-border tw-p-4 tw-transition-colors"
        :class="termsAccepted ? 'tw-border-emerald-200 tw-bg-emerald-50' : 'tw-border-slate-200 tw-bg-slate-50'"
      >
        <div class="tw-flex tw-items-center tw-justify-between tw-gap-3">
          <div class="tw-flex tw-items-center tw-gap-3">
            <div
              class="tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-full"
              :class="termsAccepted ? 'tw-bg-emerald-100' : 'tw-bg-slate-200'"
            >
              <svg
                v-if="termsAccepted"
                class="tw-h-4 tw-w-4 tw-text-emerald-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M5 13l4 4L19 7"
                />
              </svg>
              <svg
                v-else
                class="tw-h-4 tw-w-4 tw-text-slate-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <div>
              <p
                class="tw-text-sm tw-font-medium"
                :class="termsAccepted ? 'tw-text-emerald-800' : 'tw-text-slate-700'"
              >
                {{ termsAccepted ? 'Terms and conditions accepted' : 'Terms and conditions' }}
              </p>
              <p
                v-if="!termsAccepted"
                class="tw-text-xs tw-text-slate-500"
              >
                Please read and accept before continuing
              </p>
            </div>
          </div>
          <button
            type="button"
            class="tw-inline-flex tw-items-center tw-rounded-md tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-shadow-sm focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-offset-2 tw-transition-colors"
            :class="termsAccepted 
              ? 'tw-border tw-border-slate-200 tw-bg-white tw-text-slate-700 hover:tw-bg-slate-50 focus:tw-ring-slate-500' 
              : 'tw-border tw-border-transparent tw-bg-blue-600 tw-text-white hover:tw-bg-blue-700 focus:tw-ring-blue-500'"
            @click="$emit('open-terms')"
          >
            {{ termsAccepted ? 'Review' : 'Accept Terms' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Preferred delivery week -->
    <div class="tw-mb-4">
      <div class="tw-rounded-lg tw-border tw-border-slate-200 tw-bg-slate-50 tw-p-4">
        <div class="tw-flex tw-items-center tw-justify-between tw-mb-2">
          <label class="tw-text-sm tw-font-medium tw-text-slate-700">
            Preferred delivery week (week commencing)
          </label>
          <a
            v-if="leadTimesUrl"
            :href="leadTimesUrl"
            target="_blank"
            rel="noopener"
            class="tw-text-xs tw-text-blue-600 hover:tw-underline"
          >
            See lead times
          </a>
        </div>
        <select
          :value="preferredDeliveryWeek"
          class="tw-w-full tw-border tw-border-slate-300 tw-p-2 tw-rounded tw-text-sm focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500"
          @change="$emit('update:preferredDeliveryWeek', $event.target.value)"
        >
          <option
            v-for="opt in availableDeliveryWeeks"
            :key="opt.value"
            :value="opt.value"
          >
            {{ opt.label }}
          </option>
        </select>
        <p class="tw-mt-2 tw-text-xs tw-text-slate-500">
          Earliest selectable week is at least 2 weeks from today, the delivery will be confirmed by us after order is placed.
        </p>
      </div>
    </div>

    <!-- Promotion banner -->
    <div
      v-if="promoBannerImage"
      class="tw-bg-white tw-rounded-lg tw-border tw-border-dashed tw-border-emerald-300 tw-p-4 tw-mb-4 tw-flex tw-items-center tw-justify-center"
    >
      <img
        :src="promoBannerImage"
        :alt="promoBannerAlt"
        class="tw-w-full tw-max-h-32 tw-object-contain tw-rounded-md tw-bg-slate-50"
        loading="lazy"
      >
    </div>

    <button
      :disabled="!canContinue"
      :class="[
        'tw-w-full tw-py-3 tw-px-4 tw-rounded-md tw-font-medium tw-text-sm tw-transition-colors',
        canContinue
          ? 'tw-bg-blue-600 tw-text-white hover:tw-bg-blue-700 tw-shadow-sm'
          : 'tw-bg-slate-200 tw-text-slate-400 tw-cursor-not-allowed'
      ]"
      @click="$emit('confirm')"
    >
      Continue to payment
    </button>

    <!-- Validation message -->
    <p
      v-if="!canContinue"
      class="tw-mt-2 tw-text-xs tw-text-slate-500 tw-text-center"
    >
      <span v-if="!confirmationChecked">Please confirm the specification is correct</span>
      <span v-else-if="!approvalValid">Please confirm you are authorised and provide your details</span>
      <span v-else-if="!termsAccepted">Please accept the terms and conditions</span>
      <span v-else-if="!preferredDeliveryWeek">Please select a preferred delivery week</span>
    </p>
  </div>
</template>

<script>
import { computed } from 'vue'

export default {
  name: 'ReviewConfirmStep',
  props: {
    confirmationChecked: {
      type: Boolean,
      default: false
    },
    approvedToSign: {
      type: Boolean,
      default: false
    },
    approverName: {
      type: String,
      default: ''
    },
    approverEmail: {
      type: String,
      default: ''
    },
    termsAccepted: {
      type: Boolean,
      default: false
    },
    preferredDeliveryWeek: {
      type: String,
      default: ''
    },
    availableDeliveryWeeks: {
      type: Array,
      default: () => []
    },
    leadTimesUrl: {
      type: String,
      default: ''
    },
    promoBannerImage: {
      type: String,
      default: ''
    },
    promoBannerAlt: {
      type: String,
      default: 'Special offer'
    },
    canRequestAmendments: {
      type: Boolean,
      default: true
    }
  },
  emits: [
    'update:confirmationChecked',
    'update:approvedToSign',
    'update:approverName',
    'update:approverEmail',
    'update:preferredDeliveryWeek',
    'open-expedite',
    'open-amendments',
    'open-terms',
    'confirm'
  ],
  setup(props) {
    const approvalValid = computed(() => {
      if (!props.approvedToSign) return false
      return props.approverName.trim() !== '' && props.approverEmail.trim() !== ''
    })

    const canContinue = computed(() => {
      return props.confirmationChecked && approvalValid.value && props.termsAccepted && props.preferredDeliveryWeek
    })

    return {
      approvalValid,
      canContinue
    }
  }
}
</script>
